/**
 * Single source of truth for every Blender asset's "interface": node names and markup.
 * Pure data (no three.js) so both the runtime (stubs, loader) and Node tools (validator) import it.
 */
export const EMISSIVE_GROUPS = ['lantern', 'window', 'vending'] as const;
export type EmissiveGroup = (typeof EMISSIVE_GROUPS)[number];

export const WIND_KINDS = ['shide', 'branch', 'wire'] as const;
export type WindKind = (typeof WIND_KINDS)[number];

export interface Markup {
  snow?: boolean;
  wet?: boolean;
  emissive?: EmissiveGroup;
  wind?: WindKind;
}

export const ASSET_BUDGETS = {
  hero: { maxTriangles: 30_000, maxTexture: 2048 },
  mid: { maxTriangles: 10_000, maxTexture: 1024 },
  far: { maxTriangles: 2_000, maxTexture: 1024 },
} as const;
export type AssetTier = keyof typeof ASSET_BUDGETS;

export interface CatalogEntry {
  tier: AssetTier;
  /** File name under public/assets/. */
  file: string;
  /** Child node name -> markup. The root node is named after the catalog key. */
  nodes: Record<string, Markup>;
}

/** Leaf/blossom attach points exported by tools/blender/sakura_v0.py (must match its N_TIPS). */
export const SAKURA_TIPS = 72;
export const sakuraTipName = (i: number): string => `sakura_v0_tip_${String(i).padStart(3, '0')}`;

export const ASSET_CATALOG = {
  toro: {
    tier: 'mid',
    file: 'toro.glb',
    nodes: {
      toro_base: {},
      toro_post: {},
      toro_platform: { snow: true },
      toro_firebox: {},
      toro_light: { emissive: 'lantern' },
      toro_roof: { snow: true },
      toro_jewel: { snow: true },
    },
  },
  torii: {
    tier: 'hero',
    file: 'torii.glb',
    nodes: {
      torii_pillar_l: {},
      torii_pillar_r: {},
      torii_kasagi: { snow: true },
      torii_shimaki: {},
      torii_nuki: { snow: true },
      torii_gakuzuka: {},
      torii_kusabi: {},
      torii_base_l: {},
      torii_base_r: {},
    },
  },
  haiden_edge: {
    tier: 'hero',
    file: 'haiden_edge.glb',
    nodes: {
      haiden_edge_floor: {},
      haiden_edge_posts: {},
      haiden_edge_roof: { snow: true },
      haiden_edge_shimenawa: {},
      haiden_edge_shide: { wind: 'shide' },
      haiden_edge_chochin_l: { emissive: 'lantern' },
      haiden_edge_chochin_r: { emissive: 'lantern' },
      haiden_edge_ema_mount: {},
    },
  },
  utility_pole: {
    tier: 'mid',
    file: 'utility_pole.glb',
    nodes: {
      utility_pole_post: {},
      utility_pole_arm: {},
      utility_pole_insulators: {},
      utility_pole_lamp_arm: {},
      utility_pole_lamp: { emissive: 'lantern' },
      utility_pole_wire_a: {},
      utility_pole_wire_b: {},
    },
  },
  vending: {
    tier: 'mid',
    file: 'vending.glb',
    nodes: { vending_body: {}, vending_panel: { emissive: 'vending' } },
  },
  sakura_v0: {
    tier: 'hero',
    file: 'sakura_v0.glb',
    nodes: {
      sakura_v0_trunk: { wind: 'branch' },
      ...Object.fromEntries(Array.from({ length: SAKURA_TIPS }, (_, i) => [sakuraTipName(i), {}])),
    },
  },
  fence_segment: {
    tier: 'mid',
    file: 'fence_segment.glb',
    nodes: { fence_segment_post: { snow: true }, fence_segment_rails: { snow: true } },
  },
} satisfies Record<string, CatalogEntry>;

export type AssetName = keyof typeof ASSET_CATALOG;
